import { useEffect, useState } from 'preact/hooks';
import { Bot, Copy, DoorOpen, GraduationCap, Hammer, KeyRound, Lock, Map as MapIcon, Play as PlayIcon, RefreshCw, Share2, Swords, Target, Users, X } from 'lucide-preact';
import { MODE_INFO, type ModeInfo } from '@vm/content';
import type { ModeId, RoomInfo } from '@vm/shared';
import { api } from '../api/routes';
import { audio } from '../../game/audio/Audio';
import { copyText, shareText } from '../platform';
import { mapName, mapsForMode, onlineModes, practiceModes } from '../state/content';
import { openModal, replace, route } from '../state/nav';
import { cancelQueue, createRoom, enterRoom, isPartyMember, joinRoom, quickPlay, roomReady, startOffline, startSandbox, startTutorial } from '../state/play';
import { prefs, updatePrefs } from '../state/prefs';
import { syncPartyMode } from '../state/socialActions';
import { isOffline, queue, restricted, toast } from '../state/store';
import { fmtClock } from '../ui/format';
import { useAsync, useBusy, useNow } from '../ui/hooks';
import { Icon } from '../ui/icons';
import { AsyncButton, Empty, ErrorState, Field, Modal, OnlineOnly, Screen, Segmented, Slider, Spinner, Tabs, Toggle } from '../ui/kit';

type Tab = 'quick' | 'rooms' | 'practice';

const MODE_TONES: Partial<Record<ModeId, string>> = {
  tdm: 'red',
  ffa: 'orange',
  dom: 'blue',
  ctf: 'green',
  demolition: 'pink',
  siege: 'brown',
  br: 'yellow',
  zombies: 'purple',
  duel: 'cyan',
  gungame: 'lime',
  sandbox: 'teal',
  practice: 'grey',
};

const SKILLS = [
  { value: 0.25, label: 'Easy' },
  { value: 0.5, label: 'Normal' },
  { value: 0.75, label: 'Hard' },
  { value: 0.95, label: 'Elite' },
];

function nearestSkill(v: number): number {
  return SKILLS.reduce((a, b) => (Math.abs(b.value - v) < Math.abs(a.value - v) ? b : a)).value;
}

function ModeCard(props: { mode: ModeInfo; selected: boolean; onClick: () => void; note?: string }) {
  const m = props.mode;
  return (
    <button class={`mode-card tone-${MODE_TONES[m.id] ?? 'grey'} ${props.selected ? 'is-on' : ''}`} onClick={props.onClick}>
      <span class="mode-card-icon">
        <Icon name={m.icon} size={30} strokeWidth={2.4} />
      </span>
      <span class="mode-card-body">
        <span class="mode-card-name">{m.name}</span>
        <span class="mode-card-desc">{m.description}</span>
        <span class="mode-card-meta">
          <span>
            <Users size={13} /> {m.teams > 1 ? `${m.maxPlayers / 2}v${m.maxPlayers / 2}` : `${m.maxPlayers} players`}
          </span>
          {m.building && (
            <span>
              <Hammer size={13} /> Building
            </span>
          )}
          {props.note && <span class="mode-card-note">{props.note}</span>}
        </span>
      </span>
    </button>
  );
}

function MapPicker(props: { mode: ModeId; value: string; onChange: (id: string) => void; allowAny?: boolean }) {
  const maps = mapsForMode(props.mode);
  if (!maps.length) return <p class="muted small">The server picks the map for this mode.</p>;
  return (
    <div class="map-picker">
      {props.allowAny && (
        <button class={`map-chip ${props.value === '' ? 'is-on' : ''}`} onClick={() => props.onChange('')}>
          <MapIcon size={16} /> Any map
        </button>
      )}
      {maps.map((m) => (
        <button key={m.id} class={`map-chip ${props.value === m.id ? 'is-on' : ''}`} title={m.description} onClick={() => props.onChange(m.id)}>
          {m.name}
        </button>
      ))}
    </div>
  );
}

// Quick play ---------------------------------------------------------------------------------------------------------

function QuickPlay() {
  const modes = onlineModes.value;
  const selected = modes.find((m) => m.id === prefs.value.mode) ?? modes[0];
  const q = queue.value;
  const now = useNow(q.state === 'searching' ? 500 : 60000);
  if (!selected) return <Empty icon={<Swords size={44} />} title="No modes available" body="Matchmaking is taking a break. Try Practice while you wait." />;
  // A remembered map that live-ops has since switched off falls back to "Any map".
  const pref = prefs.value.maps[selected.id] ?? '';
  const map = pref && mapsForMode(selected.id).some((m) => m.id === pref) ? pref : '';
  const member = isPartyMember();
  return (
    <div class="split">
      <div class="split-main scr-scroll">
        <div class="mode-grid">
          {modes.map((m) => (
            <ModeCard
              key={m.id}
              mode={m}
              selected={m.id === selected.id}
              onClick={() => {
                audio.play('ui_click');
                updatePrefs({ mode: m.id });
                void syncPartyMode(m.id);
              }}
            />
          ))}
        </div>
      </div>
      <div class={`split-side play-side tone-${MODE_TONES[selected.id] ?? 'grey'}`}>
        <div class="play-side-scroll">
          <div class="play-side-head">
            <span class="play-side-icon">
              <Icon name={selected.icon} size={36} />
            </span>
            <div>
              <h2>{selected.name}</h2>
              <p>{selected.description}</p>
            </div>
          </div>
          <h4 class="section-title">Map</h4>
          <MapPicker mode={selected.id} value={map} allowAny onChange={(id) => updatePrefs({ maps: { ...prefs.value.maps, [selected.id]: id } })} />
        </div>
        <div class="play-side-foot">
          {q.state === 'searching' ? (
            <div class="queue-box">
              <div>
                <b>Searching for {MODE_INFO[q.mode]?.name ?? q.mode}…</b>
                <span>
                  {fmtClock((now - q.since) / 1000)}
                  {q.estimate > 0 && ` · about ${fmtClock(q.estimate)}`}
                </span>
              </div>
              <button class="btn danger" onClick={() => cancelQueue()}>
                <X size={18} /> Cancel
              </button>
            </div>
          ) : (
            <button class="btn primary big play-go" disabled={member} onClick={() => quickPlay(selected.id, map || undefined)}>
              <PlayIcon size={24} fill="currentColor" strokeWidth={0} /> {member ? 'Leader starts' : 'Quick Play'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Private rooms ------------------------------------------------------------------------------------------------------

function CreateRoom() {
  const r = prefs.value.room;
  const modes = onlineModes.value.filter((m) => mapsForMode(m.id).length > 0);
  const mode = modes.find((m) => m.id === r.mode) ?? modes[0];
  const [password, setPassword] = useState('');
  const set = (patch: Partial<typeof r>): void => updatePrefs({ room: { ...r, ...patch } });
  if (!mode) return <Empty title="Rooms unavailable" />;
  const maps = mapsForMode(mode.id);
  const mapId = maps.some((m) => m.id === r.mapId) ? r.mapId : maps[0]?.id ?? '';
  const maxCap = mode.maxPlayers;
  const maxPlayers = Math.min(r.maxPlayers, maxCap);
  return (
    <div class="panel room-create">
      <h3 class="panel-title">
        <DoorOpen size={20} /> Create a room
      </h3>
      <Field label="Mode">
        <select
          class="ui-input"
          value={mode.id}
          onChange={(e) => {
            const id = (e.currentTarget as HTMLSelectElement).value as ModeId;
            const info = MODE_INFO[id];
            set({ mode: id, maxPlayers: info.maxPlayers, timeLimit: info.timeLimit, scoreLimit: info.scoreLimit, mapId: mapsForMode(id)[0]?.id ?? '' });
          }}
        >
          {modes.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Map">
        <MapPicker mode={mode.id} value={mapId} onChange={(id) => set({ mapId: id })} />
      </Field>
      <div class="form-grid">
        <Slider label="Max players" value={maxPlayers} min={2} max={maxCap} step={mode.teams > 1 ? 2 : 1} onChange={(v) => set({ maxPlayers: v, bots: Math.min(r.bots, v - 1) })} />
        <Slider label="Bots" value={Math.min(r.bots, maxPlayers - 1)} min={0} max={maxPlayers - 1} step={1} onChange={(v) => set({ bots: v })} />
        <Slider label="Time limit" value={Math.round(r.timeLimit / 60)} min={0} max={20} step={1} format={(v) => (v === 0 ? 'None' : `${v} min`)} onChange={(v) => set({ timeLimit: v * 60 })} />
        <Slider label="Score limit" value={r.scoreLimit} min={0} max={Math.max(10, MODE_INFO[mode.id].scoreLimit * 2)} step={1} format={(v) => (v === 0 ? 'None' : String(v))} onChange={(v) => set({ scoreLimit: v })} />
      </div>
      <Field label="Bot skill">
        <Segmented options={SKILLS} value={nearestSkill(r.botSkill)} onChange={(v) => set({ botSkill: v })} />
      </Field>
      <div class="form-grid">
        <Field label="Password (optional)" hint="Only friends with the password can join.">
          <input class="ui-input" type="text" maxLength={16} value={password} autoComplete="off" onInput={(e) => setPassword((e.currentTarget as HTMLInputElement).value)} />
        </Field>
        <Toggle label="Show in public list" checked={r.public && !restricted.value} disabled={restricted.value} onChange={(v) => set({ public: v })} />
      </div>
      <AsyncButton
        class="primary big"
        onClick={() =>
          createRoom({
            mode: mode.id,
            mapId,
            maxPlayers,
            bots: Math.min(r.bots, maxPlayers - 1),
            botSkill: r.botSkill,
            timeLimit: r.timeLimit,
            scoreLimit: r.scoreLimit,
            password: password.trim() || undefined,
            public: r.public && !restricted.value,
          })
        }
      >
        <DoorOpen size={22} /> Create room
      </AsyncButton>
    </div>
  );
}

function PasswordPrompt(props: { room: RoomInfo; close: () => void }) {
  const [pw, setPw] = useState('');
  return (
    <Modal title={`Join ${props.room.code}`} icon={<Lock size={20} />} size="sm" onClose={props.close}>
      <Field label="Room password">
        <input class="ui-input" type="text" maxLength={16} autoFocus value={pw} onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
      </Field>
      <div class="ui-modal-actions">
        <button class="btn" onClick={props.close}>
          Cancel
        </button>
        <AsyncButton
          class="primary"
          disabled={!pw}
          onClick={async () => {
            if (await joinRoom(props.room.code, pw)) props.close();
          }}
        >
          Join
        </AsyncButton>
      </div>
    </Modal>
  );
}

function askPassword(room: RoomInfo): void {
  openModal((close) => <PasswordPrompt room={room} close={close} />);
}

function JoinRoom() {
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [busy, run] = useBusy();
  const list = useAsync(async () => (await api.rooms.listPublic()).rooms, []);
  const valid = code.trim().length >= 4;
  return (
    <div class="room-join">
      <form
        class="panel"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) void run(() => joinRoom(code, pw));
        }}
      >
        <h3 class="panel-title">
          <KeyRound size={20} /> Join with a code
        </h3>
        <div class="form-grid">
          <Field label="Room code">
            <input
              class="ui-input big code-input"
              value={code}
              maxLength={8}
              placeholder="ABC123"
              autoComplete="off"
              spellcheck={false}
              onInput={(e) => setCode((e.currentTarget as HTMLInputElement).value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            />
          </Field>
          <Field label="Password (if any)">
            <input class="ui-input" value={pw} maxLength={16} autoComplete="off" onInput={(e) => setPw((e.currentTarget as HTMLInputElement).value)} />
          </Field>
        </div>
        <button class="btn primary" type="submit" disabled={!valid || busy}>
          <PlayIcon size={18} fill="currentColor" strokeWidth={0} /> Join room
        </button>
      </form>
      <div class="panel">
        <h3 class="panel-title">
          <Users size={20} /> Open rooms
          <button class="btn sm icon push-right" aria-label="Refresh" onClick={list.reload}>
            <RefreshCw size={16} class={list.loading ? 'ui-spin' : ''} />
          </button>
        </h3>
        {list.loading && !list.data ? (
          <Spinner />
        ) : list.error ? (
          <ErrorState message={list.error} onRetry={list.reload} />
        ) : !list.data?.length ? (
          <p class="muted small">No public rooms right now. Create one and invite your friends!</p>
        ) : (
          <div class="room-list">
            {list.data.map((r) => (
              <div key={r.code} class="room-row">
                <span class="room-row-mode">
                  <Icon name={MODE_INFO[r.mode]?.icon ?? 'swords'} size={20} />
                </span>
                <span class="room-row-text">
                  <b>
                    {MODE_INFO[r.mode]?.name ?? r.mode} · {mapName(r.mapId)}
                  </b>
                  <small>
                    {r.host} · {r.players}/{r.maxPlayers} players
                  </small>
                </span>
                {r.hasPassword && <Lock size={16} class="muted" />}
                <AsyncButton class="sm green" disabled={r.players >= r.maxPlayers} onClick={async () => (r.hasPassword ? askPassword(r) : void (await joinRoom(r.code)))}>
                  Join
                </AsyncButton>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Rooms() {
  return (
    <div class="rooms scr-scroll">
      <CreateRoom />
      <JoinRoom />
    </div>
  );
}

// Practice -----------------------------------------------------------------------------------------------------------

function Practice() {
  const p = prefs.value.practice;
  const modes = practiceModes.value;
  const mode = modes.find((m) => m.id === p.mode) ?? modes[0];
  const set = (patch: Partial<typeof p>): void => updatePrefs({ practice: { ...p, ...patch } });
  if (!mode) return <Empty title="Practice unavailable" />;
  const maps = mapsForMode(mode.id);
  const mapId = maps.some((m) => m.id === p.mapId) ? p.mapId : maps[0]?.id ?? 'training';
  const maxBots = Math.max(1, MODE_INFO[mode.id].maxPlayers - 1);
  const bots = Math.min(p.bots, maxBots);
  return (
    <div class="practice scr-scroll">
      <div class="practice-cards">
        <button class="practice-card tone-green" onClick={() => startTutorial()}>
          <GraduationCap size={40} />
          <span>
            <b>Boot Camp</b>
            <small>Learn the basics step by step</small>
          </span>
        </button>
        <button class="practice-card tone-teal" onClick={() => startSandbox('training')}>
          <Hammer size={40} />
          <span>
            <b>Sandbox</b>
            <small>Build and blow things up freely</small>
          </span>
        </button>
      </div>
      <div class="panel">
        <h3 class="panel-title">
          <Bot size={20} /> Practice vs bots
          <span class="panel-note">Offline · no rewards</span>
        </h3>
        <div class="mode-grid compact">
          {modes.map((m) => (
            <ModeCard
              key={m.id}
              mode={m}
              selected={m.id === mode.id}
              onClick={() => {
                audio.play('ui_click');
                set({ mode: m.id, mapId: mapsForMode(m.id)[0]?.id ?? p.mapId });
              }}
            />
          ))}
        </div>
        <Field label="Map">
          <MapPicker mode={mode.id} value={mapId} onChange={(id) => set({ mapId: id })} />
        </Field>
        <div class="form-grid">
          <Slider label="Bots" value={bots} min={0} max={maxBots} step={1} onChange={(v) => set({ bots: v })} />
          <Field label="Difficulty">
            <Segmented options={SKILLS} value={nearestSkill(p.skill)} onChange={(v) => set({ skill: v })} />
          </Field>
        </div>
        <button
          class="btn primary big"
          onClick={() => {
            audio.unlock();
            startOffline(mode.id, mapId, bots + 1, { skill: p.skill });
          }}
        >
          <Target size={22} /> Start practice
        </button>
      </div>
    </div>
  );
}

export function PlayScreen() {
  const offline = isOffline.value;
  const tab = ((route.value.params.tab as Tab) || (offline ? 'practice' : 'quick')) as Tab;
  return (
    <Screen
      title="Play"
      icon={<PlayIcon size={26} fill="currentColor" strokeWidth={0} />}
      tabs={
        <Tabs<Tab>
          value={tab}
          onChange={(t) => replace('play', { tab: t })}
          tabs={[
            { id: 'quick', label: 'Quick Play', icon: <Swords size={18} /> },
            { id: 'rooms', label: 'Private Room', icon: <KeyRound size={18} /> },
            { id: 'practice', label: 'Practice', icon: <Target size={18} /> },
          ]}
        />
      }
    >
      <div class="play-body" key={tab}>
        {tab === 'quick' && (
          <OnlineOnly feature="Online play">
            <QuickPlay />
          </OnlineOnly>
        )}
        {tab === 'rooms' && (
          <OnlineOnly feature="Private rooms">
            <Rooms />
          </OnlineOnly>
        )}
        {tab === 'practice' && <Practice />}
      </div>
    </Screen>
  );
}

// Room ready dialog --------------------------------------------------------------------------------------------------

export function RoomReadyDialog(props: { close: () => void }) {
  const r = roomReady.value;
  const now = useNow(1000);
  useEffect(() => {
    if (!r) props.close();
  }, [r]);
  if (!r) return null;
  const info = MODE_INFO[r.match.mode];
  const age = Math.floor((now - r.createdAt) / 1000);
  return (
    <Modal
      title="Room ready!"
      icon={<DoorOpen size={22} />}
      size="sm"
      onClose={() => {
        roomReady.value = null;
        props.close();
      }}
    >
      <div class="room-ready">
        <p class="muted">
          {info?.name} on {mapName(r.match.mapId)}. Friends can join from <b>Play → Private Room</b> with this code:
        </p>
        <div class="room-code">{r.code || '—'}</div>
        <div class="room-ready-actions">
          <button
            class="btn"
            disabled={!r.code}
            onClick={async () => {
              if (await copyText(r.code)) toast('Room code copied', { kind: 'success' });
            }}
          >
            <Copy size={18} /> Copy
          </button>
          <button
            class="btn"
            disabled={!r.code}
            onClick={async () => {
              const res = await shareText('Voxel Marines room', `Join my Voxel Marines room! Code: ${r.code}`);
              if (res === 'copied') toast('Invite copied', { kind: 'success' });
            }}
          >
            <Share2 size={18} /> Share
          </button>
        </div>
        <AsyncButton
          class="primary big"
          onClick={async () => {
            await enterRoom();
            props.close();
          }}
        >
          <PlayIcon size={22} fill="currentColor" strokeWidth={0} /> Enter room
        </AsyncButton>
        {age > 40 && <small class="muted">We'll grab a fresh pass when you enter.</small>}
      </div>
    </Modal>
  );
}
